import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '@shared/config/routes';
import { Helmet } from 'react-helmet-async';
import { Toaster } from '@shared/components/ui/toaster';
import ErrorBoundary from '@shared/components/ErrorBoundary';
import { ThemeProvider } from '@shared/hooks/useTheme';
import { AuthProvider, useAuth } from '@shared/contexts/AuthContext';
import DepotSidebar from '@/components/DepotSidebar';
import DepotHeader from '@/components/DepotHeader';
import DepotHome from '@/components/DepotHome';
import LoginPage from '@/components/LoginPage';
import ResetPasswordPage from '@/components/ResetPasswordPage';
import ChangePasswordPage from '@/components/ChangePasswordPage';
import PlaceholderPage from '@/components/PlaceholderPage';
import EmergencyRollCall from '@/components/EmergencyRollCall';
import QRCodeGenerator from '@/components/QRCodeGenerator';
import InductionBriefEditor from '@/components/InductionBriefEditor';
import InductionInviteManager from '@/components/InductionInviteManager';
import IncidentReporting from '@/components/IncidentReporting';
import KioskScanner from '@/components/KioskScanner';
import AnalyticsDashboard from '@/components/AnalyticsDashboard';
import SystemSettings from '@/components/SystemSettings';
import SiteManagement from '@/components/SiteManagement';
import ProjectsManagement from '@/components/ProjectsManagement';
import AuditHub from '@/components/audit-hub/AuditHub';
import MyActions from '@/components/audit-hub/MyActions';
import HelpModal from '@/components/HelpModal';
import UserManagementPage from '@/pages/UserManagementPage';
import { supabase } from '@shared/supabaseClient';

/**
 * Staff App — authenticated depot management UI.
 */
function DepotApp() {
  const { user, loading, signOut } = useAuth();
  const location = useLocation();
  const [showHelp, setShowHelp] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [stats, setStats] = useState({
    openIncidentsCount: 0,
    currentOccupancy: 0,
    pendingProjectsCount: 0,
    pendingInvitesCount: 0,
    pendingExtensionReviews: 0,
    myOpenActionsCount: 0,
  });

  useEffect(() => {
    if (!user?.depot_id) return;

    const isAdminRole = ['admin', 'owner', 'system-admin'].includes(user?.role);

    const fetchStats = async () => {
      try {
        // Projects awaiting staff review
        let projectsQuery = supabase.from('projects').select('*', { count: 'exact', head: true }).eq('status', 'submitted_awaiting_approval');
        if (!isAdminRole) projectsQuery = projectsQuery.eq('site_id', user.depot_id);

        // Pending invites count — try RPC first (excludes project team members),
        // fall back to direct query if the RPC isn't deployed yet.
        let invitesCount = 0;
        try {
          const { data, error } = await supabase.rpc('staff_count_pending_invites', {
            p_depot_id: isAdminRole ? null : (user.depot_id || null),
          });
          if (!error && data != null) {
            invitesCount = data;
          } else {
            throw new Error('RPC unavailable');
          }
        } catch {
          // Fallback: direct count excluding TM- project team members
          let q = supabase.from('induction_submissions').select('*', { count: 'exact', head: true }).eq('status', 'submitted');
          if (!isAdminRole) q = q.eq('depot_id', user.depot_id);
          q = q.not('public_reference', 'like', 'TM-%');
          const { count } = await q;
          invitesCount = count || 0;
        }

        const [{ count: projectsCount }, { data: sessions }, { data: incidents }] = await Promise.all([
          projectsQuery,
          supabase.from('site_sessions').select('id').eq('depot_id', user.depot_id).is('checked_out_at', null),
          supabase.from('incidents').select('id').eq('depot_id', user.depot_id).in('status', ['open', 'investigating']),
        ]);

        // Count pending extension requests to review (where user assigned the action)
        let extensionReviewCount = 0;
        try {
          const { count } = await supabase
            .from('action_extension_requests')
            .select('*, audit_actions!inner(assigned_by)', { count: 'exact', head: true })
            .eq('status', 'pending')
            .eq('audit_actions.assigned_by', user.id);
          extensionReviewCount = count || 0;
        } catch { /* table may not exist yet */ }

        // Count open actions assigned to current user
        let myActionsCount = 0;
        try {
          const { count } = await supabase
            .from('audit_actions')
            .select('*', { count: 'exact', head: true })
            .eq('assigned_to', user.id)
            .in('status', ['assigned', 'in_progress', 'rejected']);
          myActionsCount = count || 0;
        } catch { /* table may not exist yet */ }

        setStats({
          currentOccupancy: sessions?.length || 0,
          openIncidentsCount: incidents?.length || 0,
          pendingProjectsCount: projectsCount || 0,
          pendingInvitesCount: invitesCount,
          pendingExtensionReviews: extensionReviewCount,
          myOpenActionsCount: myActionsCount,
        });
      } catch (err) {
        // Non-fatal
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleLogout = async () => { await signOut(); };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'hsl(0 0% 10%)' }}>
        <div className="text-center">
          <img src="/assets/transparent.png" alt="Loading" className="h-24 w-auto mx-auto mb-4 animate-pulse" />
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={ROUTES.AUTH.LOGIN} state={{ from: location }} replace />;
  }

  return (
    <ThemeProvider>
      <ErrorBoundary>
        <Helmet>
          <title>Atlas — Site Management</title>
          <meta name="description" content="Site induction, visitor management, and safety system" />
        </Helmet>

        <div className="flex h-screen bg-background overflow-hidden">
          {sidebarOpen && (
            <div className="md:hidden fixed inset-0 z-50 flex">
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
              <aside className="dark relative w-full h-full flex flex-col bg-card border-r border-border z-10 overflow-y-auto">
                <DepotSidebar user={user} openIncidentsCount={stats.openIncidentsCount} currentOccupancy={stats.currentOccupancy} pendingProjectsCount={stats.pendingProjectsCount} pendingInvitesCount={stats.pendingInvitesCount} pendingExtensionReviews={stats.pendingExtensionReviews} myOpenActionsCount={stats.myOpenActionsCount} onLogout={handleLogout} onHelpClick={() => { setSidebarOpen(false); setShowHelp(true); }} onClose={() => setSidebarOpen(false)} />
              </aside>
            </div>
          )}

          <aside className="dark hidden md:flex flex-shrink-0 w-[280px] h-screen border-r border-border flex-col bg-card z-30">
            <DepotSidebar user={user} openIncidentsCount={stats.openIncidentsCount} currentOccupancy={stats.currentOccupancy} pendingProjectsCount={stats.pendingProjectsCount} pendingInvitesCount={stats.pendingInvitesCount} pendingExtensionReviews={stats.pendingExtensionReviews} myOpenActionsCount={stats.myOpenActionsCount} onLogout={handleLogout} onHelpClick={() => setShowHelp(true)} />
          </aside>

          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            <DepotHeader user={user} currentOccupancy={stats.currentOccupancy} openIncidentsCount={stats.openIncidentsCount} onLogout={handleLogout} onHelpClick={() => setShowHelp(true)} onMenuClick={() => setSidebarOpen(true)} />

            <main className="flex-1 overflow-y-auto bg-background">
              <Routes>
                <Route index element={<Navigate to="home" replace />} />
                <Route path="home" element={<DepotHome />} />
                <Route path="projects" element={<ProjectsManagement />} />
                <Route path="dashboard" element={<AnalyticsDashboard />} />
                <Route path="analytics" element={<AnalyticsDashboard />} />
                <Route path="roll-call" element={<EmergencyRollCall />} />
                <Route path="kiosk" element={<KioskScanner />} />
                <Route path="incidents" element={<IncidentReporting />} />
                <Route path="visitors" element={<InductionInviteManager />} />
                <Route path="induction-briefs" element={<InductionBriefEditor />} />
                <Route path="qr-codes" element={<QRCodeGenerator />} />
                <Route path="audit-hub" element={<AuditHub />} />
                <Route path="my-actions" element={<MyActions />} />
                <Route path="depots" element={<SiteManagement />} />
                <Route path="system-settings" element={<SystemSettings />} />
                <Route path="user-management" element={<UserManagementPage />} />
                <Route path="reports" element={<PlaceholderPage title="Reports" description="Generate occupancy reports, visit logs, and analytics." />} />
                <Route path="locations" element={<PlaceholderPage title="Work Areas" description="Configure work areas and zones within the depot." />} />
                <Route path="notifications" element={<PlaceholderPage title="Notifications" description="Configure email and notification settings." />} />
                <Route path="account-security" element={<PlaceholderPage title="Security" description="Manage password, two-factor authentication, and security settings." />} />
                <Route path="subscription" element={<PlaceholderPage title="Subscription" description="View and manage your subscription plan." />} />
                <Route path="*" element={<Navigate to="home" replace />} />
              </Routes>
            </main>
          </div>
        </div>

        <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} userRole={user.role} />
        <Toaster />
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Routes>
            <Route path={ROUTES.AUTH.LOGIN} element={<LoginPage />} />
            <Route path={ROUTES.AUTH.RESET_PASSWORD} element={<ResetPasswordPage />} />
            <Route path={ROUTES.AUTH.CHANGE_PASSWORD} element={<ChangePasswordPage />} />
            <Route path={`${ROUTES.APP.ROOT}/*`} element={<DepotApp />} />
            <Route path="*" element={<Navigate to={ROUTES.APP.HOME} replace />} />
          </Routes>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
